// external libs
import { useEffect, useState } from 'react'

// hooks
import useWebService from '../services/web/useWebService'
import useAuthenStore from '../stores/authen/authenStore'

// types
import type { ExampleInfomation } from '../services/web/useWebService'

/**
 * Custom React hook for fetching and downloading example information files
 * based on the current user's group.
 *
 * This hook:
 * - Fetches example files when `userGroupId` changes.
 * - Provides a `downloadExampleInfomation` method to open or download files.
 *
 * Example usage:
 * ```tsx
 * const { hasExampleInfomation, downloadExampleInfomation } = useExampleInfomation()
 *
 * return (
 *   <Button onClick={downloadExampleInfomation} disabled={!hasExampleInfomation}>
 *     Download Files
 *   </Button>
 * )
 * ```
 *
 * @returns {object} An object containing:
 * - `hasExampleInfomation`: `boolean` — Whether there are files available.
 * - `downloadExampleInfomation`: `() => void` — Function to trigger file downloads.
 */
const useExampleInfomation = () => {
    const webServices = useWebService()
    const authenStore = useAuthenStore()
    const userGroupId = authenStore.profile.userGroupId

    const [exampleInfomation, setExampleInfomation] = useState<ExampleInfomation[]>([])

    const downloadExampleInfomation = () => {
        if (exampleInfomation.length <= 0) return

        const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']

        exampleInfomation.forEach((file) => {
            if (!file?.path) return

            const url = `${file.path}?token=${file.fileToken}`
            const extension = file.filename.split('.').pop()?.toLowerCase()

            if (extension && viewableExtensions.includes(extension)) {
                window.open(url, '_blank')
            } else {
                const link = document.createElement('a')
                link.href = url
                link.download = file.filename
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            }
        })
    }

    useEffect(() => {
        const fetchExampleInfomation = async () => {
            if (!userGroupId) return

            try {
                const { result } = await webServices.getExampleInfomation(userGroupId)
                setExampleInfomation(result ?? [])
            } catch (error) {
                console.error('Error while fetching example information:', error)
            }
        }

        fetchExampleInfomation()
    }, [userGroupId])

    return {
        hasExampleInfomation: exampleInfomation.length > 0,
        downloadExampleInfomation,
    }
}

export default useExampleInfomation
